import {readQuizDraft, saveQuizDraft, clearQuizDraft} from "../../quiz-draft.js";
import {parsePhoneNumberFromString} from "libphonenumber-js/max";
import {normalizePhone, phoneProblem} from "../../phone.js";
import { useState, useRef, useEffect, type FormEvent } from "react";
import {
  calculateScaleAssessment,
  nameProblem,
  quizAnswersSchema,
  quizDataSchema,
  submissionRequestSchema,
  submissionAcceptedSchema,
  type QuizAnswers
} from "@quiz-marketing/contracts";
import { suggestEmail } from "../validation/email.js";
import { MISSING_LAST_NAME, splitFullName } from "../validation/name.js";
import {
  getResultOffer,
  getResultPage,
  quizQuestions,
  type QuizAnswerKey
} from "../config/quizConfig.js";

type LeadContact = {email: string; phone: string; firstName: string; lastName: string};
type QuizVisit = {
  analyticsSessionId: string | null; utmSource: string | null; utmMedium: string | null; utmCampaign: string | null;
  utmContent: string | null; utmTerm: string | null; clickIdType: "gclid" | "gbraid" | "wbraid" | "fbclid" | null;
  device: "mobile" | "tablet" | "desktop";
};
type PaeQuiz = {
  track: (event: string, values: Record<string, unknown>) => void;
  lead: (values: Record<string, unknown>, contact: LeadContact) => Promise<void>;
  visit: () => QuizVisit;
};
/** Visit id and stored attribution from quiz-analytics.js; without it, only the UTMs of the current URL. */
function quizVisit(): Partial<QuizVisit> {
  try { return paeQuiz()?.visit() ?? {}; } catch { return {}; }
}
const paeQuiz = () => (window as Window & {paeQuiz?: PaeQuiz}).paeQuiz;
const track = (event: string, values: Record<string, unknown> = {}) => { try { paeQuiz()?.track(event, values); } catch { /* Analytics must never block submission. */ } };
/**
 * Meta Lead / GA4 generate_lead; resolves once GTM is done (capped), so the redirect does not cut the tags.
 * The contact also prefills the booking calendar (kept for the tab only).
 */
const trackLead = async (values: Record<string, unknown>, contact: LeadContact) => { try { await paeQuiz()?.lead(values, contact); } catch { /* Analytics must never block submission. */ } };
export type FieldErrors = Record<string, string>;

const submissionContextKey = "pae-quiz-submission-v3";

type SubmissionContext = {
  idempotencyKey: string;
  sessionId: string;
  startedAt: string;
  fingerprint: string | null;
};

function createSubmissionContext(): SubmissionContext {
  return {
    idempotencyKey: crypto.randomUUID(),
    sessionId: crypto.randomUUID(),
    startedAt: new Date().toISOString(),
    fingerprint: null
  };
}

function readSubmissionContext(storage: Storage): SubmissionContext {
  try {
    const parsed = JSON.parse(storage.getItem(submissionContextKey) ?? "null") as Partial<SubmissionContext> | null;
    if (parsed && typeof parsed.idempotencyKey === "string" && typeof parsed.sessionId === "string"
      && typeof parsed.startedAt === "string" && (typeof parsed.fingerprint === "string" || parsed.fingerprint === null)) {
      return {
        idempotencyKey: parsed.idempotencyKey,
        sessionId: parsed.sessionId,
        startedAt: parsed.startedAt,
        fingerprint: parsed.fingerprint
      };
    }
  } catch { /* Browsers may deny session storage. */ }
  return createSubmissionContext();
}

function saveSubmissionContext(storage: Storage, context: SubmissionContext): void {
  try { storage.setItem(submissionContextKey, JSON.stringify(context)); } catch { /* Submission still works without storage. */ }
}

function clearSubmissionContext(storage: Storage): void {
  try { storage.removeItem(submissionContextKey); } catch { /* Submission still works without storage. */ }
}

async function fingerprintSubmission(value: unknown): Promise<string> {
  const bytes = new TextEncoder().encode(JSON.stringify(value));
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

/**
 * Field rules shared with the tour registration and the booking calendar: the same checks and Spanish messages,
 * and the GA4 reason (quiz_validation_error.error_code) uses their form_error vocabulary.
 */
type FieldProblem = { reason: string; message: string };
const fullNameProblem = (value: string): FieldProblem | null => {
  const text = value.trim().replace(/\s+/g, " ");
  if (!text) return { reason: "vacio", message: "Ingresa tu nombre y apellido." };
  const problem = nameProblem(text);
  if (problem) return { reason: "nombre_invalido", message: problem };
  return splitFullName(text) ? null : { reason: "falta_apellido", message: MISSING_LAST_NAME };
};
const emailProblem = (value: string): FieldProblem | null => {
  if (!value.trim()) return { reason: "vacio", message: "Ingresa tu correo electrónico." };
  return quizDataSchema.shape.email.safeParse(value).success ? null : { reason: "formato", message: "Ingresa un correo electrónico válido." };
};
/** The phone field's country: from an international "+…" number, or the selected flag. */
function phoneInput(form: HTMLFormElement): { value: string; country: string } {
  const data = new FormData(form);
  const value = String(data.get("phone") ?? "");
  const fromNumber = value.trim().startsWith("+") ? parsePhoneNumberFromString(value)?.country : undefined;
  return { value, country: fromNumber ?? String(data.get("country") ?? "") };
}
function contactFieldProblem(name: string, form: HTMLFormElement): FieldProblem | null {
  const input = form.elements.namedItem(name) as HTMLInputElement | null;
  if (name === "fullName") return fullNameProblem(input?.value ?? "");
  if (name === "email") return emailProblem(input?.value ?? "");
  if (name === "phone" || name === "country") { const { value, country } = phoneInput(form); return phoneProblem(value, country); }
  if (name === "consent") return input?.checked ? null : { reason: "required", message: "Debes aceptar los términos para continuar." };
  return null;
}

const EMAIL_TYPO_SUMMARY = "Revisa tu correo: ¿está bien escrito?";

function spanishValidationMessage(field: string): string {
  const messages: Record<string, string> = {
    fullName: "Ingresa tu nombre completo.",
    firstName: "Ingresa tu nombre completo.",
    lastName: "Ingresa tu apellido.",
    email: "Ingresa un correo electrónico válido.",
    phone: "Revisa el país y escribe un teléfono válido.",
    consent: "Debes aceptar los términos para continuar."
  };
  return messages[field] ?? "Revisa este campo.";
}

export function useQuizLogic(navigateToResult: (url: string) => void, turnstileEnabled: boolean) {
  const [draft] = useState(() => {
    try { return readQuizDraft(window.localStorage); } catch { return null; }
  });
  const [step, setStep] = useState(draft?.step ?? 0);
  const [answers, setAnswers] = useState<Partial<QuizAnswers>>(() => Object.fromEntries(Object.entries(draft?.answers ?? {}).filter(([,value]) => value !== undefined)) as Partial<QuizAnswers>);
  function persist(nextAnswers: Partial<QuizAnswers>, nextStep: number) {
    try { saveQuizDraft(window.localStorage, nextAnswers, nextStep); } catch { /* Sin almacenamiento, continúa normalmente. */ }
  }
  const [advancing, setAdvancing] = useState(false);
  const advanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (advanceTimer.current !== null) clearTimeout(advanceTimer.current); }, []);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileResetNonce, setTurnstileResetNonce] = useState(0);
  const submissionContext = useRef(readSubmissionContext(window.sessionStorage));
  const errorSummaryRef = useRef<HTMLDivElement>(null);

  const contactStep = quizQuestions.length;
  const totalSteps = quizQuestions.length + 1;
  const trackingStep = contactStep + 1;
  const currentQuestion = quizQuestions[step];
  const progress = ((step + 1) / totalSteps) * 100;

  const seenStep = useRef<number | null>(null);
  const enteredAt = useRef(0);
  const formStarted = useRef(false);
  const completedFields = useRef(new Set<string>());
  const successTracked = useRef(false);
  useEffect(() => {
    if (seenStep.current === step) return;
    if (seenStep.current === null) track(draft ? 'quiz_resume' : 'quiz_start', {step:step+1, restored:Boolean(draft)});
    seenStep.current = step; enteredAt.current = Date.now();
    track('quiz_step_view', {step:step+1, question_id:currentQuestion?.id ?? 'contact'});
  }, [step, currentQuestion?.id, draft]);

  function selectAnswer(questionId: QuizAnswerKey, value: string) {
    if (advanceTimer.current !== null || currentQuestion?.id !== questionId || !currentQuestion.options.some(option => option.value === value)) return;
    track('quiz_answer', {step:step+1,question_id:questionId,answer_id:value,previous_answer_id:answers[questionId] ?? 'none',elapsed_ms:Date.now()-enteredAt.current});
    const nextAnswers = { ...answers, [questionId]: value };
    const nextStep = Math.min(step + 1, contactStep);
    persist(nextAnswers, nextStep);
    setAnswers(nextAnswers);
    setAdvancing(true);
    advanceTimer.current = setTimeout(() => {
      advanceTimer.current = null;
      setStep(nextStep);
      setAdvancing(false);
    }, 550);
  }

  function goBack() {
    if (advanceTimer.current !== null) return;
    track('quiz_back',{step:step+1,elapsed_ms:Date.now()-enteredAt.current});
    setFormError("");
    setFieldErrors({});
    persist(answers, Math.max(step - 1, 0));
    setStep((current) => Math.max(current - 1, 0));
  }

  /** Shows or clears one field's message; clears the summary when no field is left with an error. */
  function setFieldProblem(field: string, problem: FieldProblem | null) {
    setFieldErrors((current) => {
      if (problem ? current[field] === problem.message : !current[field]) return current;
      const next = { ...current };
      if (problem) next[field] = problem.message; else delete next[field];
      if (!Object.keys(next).length) setFormError("");
      return next;
    });
  }

  // "¿Quisiste decir …? · No, está bien" under the email, like the tour form and the calendar.
  const [emailSuggestion, setEmailSuggestion] = useState<string | null>(null);
  const acceptedEmail = useRef("");
  const emailBlockedFor = useRef("");
  function reviewEmail(value: string): string | null {
    const typed = value.trim();
    const suggestion = typed && typed !== acceptedEmail.current ? suggestEmail(typed) : null;
    setEmailSuggestion(suggestion);
    return suggestion;
  }
  function applyEmailSuggestion() {
    const input = document.getElementById("email") as HTMLInputElement | null;
    if (!input || !emailSuggestion) return;
    input.value = emailSuggestion;
    setEmailSuggestion(null);
    setFieldProblem("email", emailProblem(input.value));
    input.focus();
  }
  function keepEmail() {
    const input = document.getElementById("email") as HTMLInputElement | null;
    acceptedEmail.current = input?.value.trim() ?? "";
    setEmailSuggestion(null);
    if (formError === EMAIL_TYPO_SUMMARY) setFormError("");
    input?.focus();
  }

  // While typing: a field already marked invalid gets its message updated (or cleared) live; others stay quiet
  // until blur, as in the tour form and the calendar.
  function updateContactField(event: FormEvent<HTMLFormElement>) {
    const input = event.target as HTMLInputElement;
    if (!['fullName','email','phone','country','consent'].includes(input.name)) return;
    if (!formStarted.current) { formStarted.current = true; track('quiz_form_start',{step:trackingStep}); }
    if (input.name === "email") { setEmailSuggestion(null); if (formError === EMAIL_TYPO_SUMMARY) setFormError(""); }
    const field = input.name === "country" ? "phone" : input.name;
    if (fieldErrors[field]) setFieldProblem(field, contactFieldProblem(field, event.currentTarget));
  }

  // On blur: explain what is wrong as soon as the person leaves a field they wrote in, and suggest email fixes.
  function completeContactField(event: FormEvent<HTMLFormElement>) {
    const input = event.target as HTMLInputElement;
    if (!['fullName','email','phone','consent'].includes(input.name)) return;
    const value = input.type === 'checkbox' ? input.checked : input.value.trim();
    if (!value) return;
    if (input.type !== 'checkbox') setFieldProblem(input.name, contactFieldProblem(input.name, event.currentTarget));
    if (input.name === "email") reviewEmail(input.value);
    if (completedFields.current.has(input.name)) return;
    completedFields.current.add(input.name);
    track('quiz_field_complete',{step:trackingStep,field_name:input.name});
  }

  async function submitContactForm(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) return;
    track('quiz_submit_attempt',{step:trackingStep});
    setFormError("");
    setFieldErrors({});

    const parsedAnswers = quizAnswersSchema.safeParse(answers);
    if (!parsedAnswers.success) {
      track('quiz_validation_error',{step:trackingStep,error_code:'missing_answers'});
      setFormError("Falta una respuesta. Vuelve al cuestionario para completarla.");
      queueMicrotask(() => errorSummaryRef.current?.focus());
      return;
    }

    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    if (turnstileEnabled && !turnstileToken) {
      track('quiz_validation_error',{step:trackingStep,field_name:'turnstile',error_code:'required'});
      setFieldErrors({ turnstileToken: "Completa la verificación de seguridad." });
      setFormError("Completa la verificación de seguridad antes de continuar.");
      queueMicrotask(() => errorSummaryRef.current?.focus());
      return;
    }
    // Same order as the tour form: name, email, phone (and consent), each with its specific message and GA4 reason.
    const problems: Record<string, FieldProblem> = {};
    for (const field of ["fullName", "email", "phone", "consent"]) {
      const problem = contactFieldProblem(field, formElement);
      if (problem) problems[field] = problem;
    }
    if (Object.keys(problems).length) {
      for (const [field, problem] of Object.entries(problems)) track('quiz_validation_error',{step:trackingStep,field_name:field,error_code:problem.reason});
      setFieldErrors(Object.fromEntries(Object.entries(problems).map(([field, problem]) => [field, problem.message])));
      setFormError("Revisa los campos marcados antes de continuar.");
      requestAnimationFrame(() => formElement.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus());
      return;
    }
    // A likely typo ("hotmial.com") stops the first submit of that address only; a second submit sends it as typed.
    const typedEmail = String(form.get("email") ?? "").trim();
    const suggestion = reviewEmail(typedEmail);
    if (suggestion && emailBlockedFor.current !== typedEmail) {
      emailBlockedFor.current = typedEmail;
      track('quiz_validation_error',{step:trackingStep,field_name:'email',error_code:'posible_error_tipeo'});
      setFormError(EMAIL_TYPO_SUMMARY);
      requestAnimationFrame(() => formElement.querySelector<HTMLElement>('[data-email-suggestion-use]')?.focus());
      return;
    }

    const phone = normalizePhone(String(form.get("phone") ?? ""), phoneInput(formElement).country);
    const fullName = String(form.get("fullName") ?? "").trim().replace(/\s+/g, " ");
    // "José Ángel Hernández" → "José Ángel" / "Hernández" (particles and common first names, as in the tour form).
    const { firstName = "", lastName = "" } = splitFullName(fullName) ?? {};
    const data = {
      firstName,
      lastName,
      email: String(form.get("email") ?? ""),
      phone: phone ?? "",
      consent: form.get("consent") === "on",
      answers: parsedAnswers.data
    };
    const query = new URLSearchParams(location.search);
    const visit = quizVisit();
    const utm = (value: string | null | undefined) => value ? value.slice(0, 120) : null;
    const country = phoneInput(formElement).country.toUpperCase();
    const clientAttributes = {
      source: "scale-readiness-quiz",
      utmSource: utm(query.get("utm_source") ?? visit.utmSource),
      utmMedium: utm(query.get("utm_medium") ?? visit.utmMedium),
      utmCampaign: utm(query.get("utm_campaign") ?? visit.utmCampaign),
      utmContent: utm(query.get("utm_content") ?? visit.utmContent),
      utmTerm: utm(query.get("utm_term") ?? visit.utmTerm),
      clickIdType: visit.clickIdType ?? null,
      ...(visit.analyticsSessionId ? { analyticsSessionId: visit.analyticsSessionId } : {}),
      ...(visit.device ? { device: visit.device } : {}),
      country: /^[A-Z]{2}$/.test(country) ? country : null,
      turnstileToken: turnstileEnabled ? turnstileToken : undefined,
      website: String(form.get("website") ?? "")
    };
    const fingerprint = await fingerprintSubmission({ formVersion: 3, data, client: clientAttributes });
    let context = submissionContext.current;
    if (context.fingerprint && context.fingerprint !== fingerprint) context = createSubmissionContext();
    context = { ...context, fingerprint };
    submissionContext.current = context;
    saveSubmissionContext(window.sessionStorage, context);
    const request = {
      formVersion: 3 as const,
      data,
      client: {
        ...clientAttributes,
        sessionId: context.sessionId,
        startedAt: context.startedAt
      }
    };

    const parsed = submissionRequestSchema.safeParse(request);
    if (!parsed.success) {
      const errors: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const rawField = String(issue.path.at(-1));
        const field = ["firstName", "lastName"].includes(rawField) ? "fullName" : rawField;
        if (!errors[field]) errors[field] = spanishValidationMessage(field);
      }
      for (const field of Object.keys(errors)) track('quiz_validation_error',{step:trackingStep,field_name:field,error_code:'invalid'});
      setFieldErrors(errors);
      setFormError("Revisa los campos marcados antes de continuar.");
      requestAnimationFrame(() => formElement.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus());
      return;
    }

    const payload = parsed.data;
    if (payload.formVersion !== 3) throw new Error("unexpected_form_version");
    setSubmitting(true);
    try {
      const response = await fetch("/api/v1/forms/contact/submissions", {
        method:"POST", headers:{"Content-Type":"application/json", "idempotency-key":context.idempotencyKey}, signal:AbortSignal.timeout(10000),
        body:JSON.stringify(payload)
      });
      if (response.status !== 202) throw new Error(`http_${response.status}`);
      const accepted = submissionAcceptedSchema.parse(await response.json());
      const {level} = calculateScaleAssessment(payload.data.answers);
      const resultOffer = getResultOffer(payload.data.answers.annualRevenue);
      if (!successTracked.current) {
        successTracked.current = true;
        const detail = {
          step:trackingStep,
          level,
          result_offer:resultOffer,
          revenue_band:payload.data.answers.annualRevenue,
          sales_qualified:resultOffer === "calendar",
          save_destination:"api",
          transaction_id:accepted.submissionId
        };
        track('quiz_submit_success',detail);
        if (detail.sales_qualified) track('quiz_qualified_lead',detail);
        await trackLead(detail, {email:payload.data.email, phone:payload.data.phone, firstName:payload.data.firstName, lastName:payload.data.lastName});
      }
      try { clearQuizDraft(window.localStorage); } catch { /* Sin almacenamiento disponible. */ }
      clearSubmissionContext(window.sessionStorage);
      navigateToResult(getResultPage(level, payload.data.answers.annualRevenue));
    } catch (error) {
      track('quiz_submit_error',{step:trackingStep,error_code:'save_failed'});
      setFormError(error instanceof Error && error.message === "http_400"
        ? "Revisa los datos ingresados y la verificación de seguridad antes de intentar de nuevo."
        : error instanceof Error && error.message === "http_429"
          ? "Espera unos segundos antes de volver a enviar. Tus datos siguen aquí."
          : "No pudimos confirmar el guardado de tus respuestas. Tus datos siguen aquí. Intenta de nuevo.");
      if (turnstileEnabled) { setTurnstileToken(""); setTurnstileResetNonce(nonce => nonce + 1); }
      requestAnimationFrame(()=>errorSummaryRef.current?.focus());
    } finally {setSubmitting(false);}
  }

  return {
    step,
    answers,
    submitting,
    advancing,
    formError,
    fieldErrors,
    turnstileToken,
    turnstileResetNonce,
    currentQuestion,
    progress,
    errorSummaryRef,
    selectAnswer,
    goBack,
    setTurnstileToken,
    submitContactForm,
    updateContactField,
    completeContactField,
    emailSuggestion,
    applyEmailSuggestion,
    keepEmail,
  };
}
